/* eslint-disable import/max-dependencies */
/* eslint-disable max-lines */
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  Ref,
  ReactElement,
} from 'react';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import TableSkeleton, { BODY_CELL_SX } from './TableSkeleton';
import Typography from '@mui/material/Typography';
import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import { isEmbedded } from 'src/util';
import { HEADER_HEIGHT } from '../HeaderX4C';
import { SxProps, Theme } from '@mui/system';
import {
  ColumnDef,
  ColumnFilter,
  ColumnSort,
  CustomTableHandle,
  Filter,
  Order,
  RenderCellParams,
  VirtualizationOptions,
} from './types';
import { ArrowNarrowUp } from '@im/ui-lib/zenith/icons';
import Stack from '@mui/material/Stack';
import FilterComponent from './FilterComponent';

interface CustomTableProps<T> {
  appliedFilters?: ColumnFilter;
  columns: ColumnDef<T>[];
  customMessage?: string;
  data: T[];
  getRowId: (row: T, index: number) => string;
  isLoading?: boolean;
  onFilterApply?: (filter: ColumnFilter) => void;
  onRowDoubleClick?: (row: T, rowId: string) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  onServerSortingChange?: (sort: ColumnSort) => void;
  paginationComponent?: React.ReactNode;
  pinnedColumns?: (keyof T)[];
  selectable?: boolean;
  selection?: string[];
  sx?: SxProps<Theme>;
  virtualizationOptions?: VirtualizationOptions;
}

const DEFAULT_COL_WIDTH = 150;
const CHECKBOX_WIDTH = 50;
const HEADER_CELL_SX = {
  backgroundColor: '#f3f6f9',
  borderBottom: '2px solid #0077D4;',
  color: '#1a2027',
  fontSize: '16px',
  fontWeight: 600,
  py: 0.5,
};

const CustomTable_ = <T,>(
  props: CustomTableProps<T>,
  ref: Ref<CustomTableHandle>
): ReactElement => {
  const {
    columns,
    customMessage,
    data,
    getRowId,
    isLoading = false,
    onSelectionChange,
    paginationComponent,
    pinnedColumns = [],
    selectable = false,
    selection,
  } = props;

  const translate = useTranslate();
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof T | ''>('');
  const [filterBy, setFilterBy] = useState<keyof T | ''>('');
  const [openFilters, setOpenFilters] = useState<Element | null>(null);

  const toggleRow = (id: string) => {
    const newSelected = new Set(selection);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    onSelectionChange?.(Array.from(newSelected));
  };

  const toggleAll = () => {
    if (selection?.length === data.length) {
      onSelectionChange?.([]);
    } else {
      const allIds = data.map(getRowId);
      onSelectionChange?.(allIds);
    }
  };

  const isAllSelected = data.length ? selection?.length === data.length : false;

  const handleSort = (columnKey: keyof T) => {
    const isAsc = orderBy === columnKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnKey);
    props.onServerSortingChange?.({
      [columnKey]: (isAsc ? 'desc' : 'asc') as Order,
    });
  };

  const sortedData = useMemo(() => {
    if (!orderBy || props.onServerSortingChange) return data;
    return [...data].sort((a, b) => {
      const valA = a[orderBy];
      const valB = b[orderBy];
      if (valA == null) return 1;
      if (valB == null) return -1;
      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, order, orderBy]);

  const pinnedColumnKeys = selectable
    ? ['__checkbox__', ...pinnedColumns]
    : [...pinnedColumns];

  const getStickyStyle = (key: keyof T | '__checkbox__', isHeader: boolean) => {
    const pinnedIndex = pinnedColumnKeys.indexOf(key as any);
    if (pinnedIndex === -1) return undefined;

    let left = 0;

    for (let i = 0; i < pinnedIndex; i++) {
      const pinnedKey = pinnedColumnKeys[i];
      if (pinnedKey === '__checkbox__') {
        left += CHECKBOX_WIDTH;
      } else {
        const colDef = columns.find((c) => c.field === pinnedKey);
        left += colDef?.width ?? DEFAULT_COL_WIDTH;
      }
    }

    const colDef = columns.find((c) => c.field === key);
    const width =
      key === '__checkbox__'
        ? CHECKBOX_WIDTH
        : colDef?.width ?? DEFAULT_COL_WIDTH;

    const getHeaderZIndex = () => {
      return key === '__checkbox__' ? 5 : 4;
    };

    const getCellZIndex = () => {
      return key === '__checkbox__' ? 3 : 2;
    };

    const isLastPinned = pinnedIndex === pinnedColumnKeys.length - 1;

    return {
      background: '#fff',
      boxSizing: 'border-box',
      left: `${left}px`,
      maxWidth: `${width}px`,
      minWidth: `${width}px`,
      position: 'sticky',
      zIndex: isHeader ? getHeaderZIndex() : getCellZIndex(),
      ...(key === '__checkbox__' && {
        '.MuiSvgIcon-root': {
          color: '#0077d4',
        },
      }),
      ...(isLastPinned ? { borderRight: '1px solid #e0e0e0' } : {}),
    };
  };

  const getColWidthStyle = (key: keyof T) => {
    const colDef = columns.find((c) => c.field === key);
    const width = colDef?.width ?? DEFAULT_COL_WIDTH;
    return {
      boxSizing: 'border-box',
      maxWidth: `${width}px`,
      minWidth: `${width}px`,
    };
  };

  useEffect(() => {
    const headerContainer = document.getElementById(
      'custom-table-header-container'
    ) as HTMLElement;
    const bodyContainer = document.getElementById(
      'custom-table-body-container'
    ) as HTMLElement;

    const syncScroll = (source: HTMLElement, target: HTMLElement) => {
      return () => {
        target.scrollLeft = source.scrollLeft;
      };
    };

    const onHeaderScroll = syncScroll(headerContainer, bodyContainer);
    const onBodyScroll = syncScroll(bodyContainer, headerContainer);

    headerContainer.addEventListener('scroll', onHeaderScroll);
    bodyContainer.addEventListener('scroll', onBodyScroll);

    return () => {
      headerContainer.removeEventListener('scroll', onHeaderScroll);
      bodyContainer.removeEventListener('scroll', onBodyScroll);
    };
  }, []);

  const bodyContainerRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: sortedData.length,
    estimateSize: () => 50,
    getScrollElement: () => bodyContainerRef.current,
    measureElement: (el) => el.getBoundingClientRect().height,
    overscan: 50,
    ...props.virtualizationOptions,
    enabled: !!props.virtualizationOptions,
  });
  const isVirtualization = !!props.virtualizationOptions;
  const rows = isVirtualization ? virtualizer.getVirtualItems() : sortedData;

  useImperativeHandle(ref, () => ({
    scrollToIndex: (index: number) => {
      virtualizer.scrollToIndex(index);
    },
  }));

  const tableRows = () => {
    return (
      <>
        {rows.map((row_, index_) => {
          const index = isVirtualization ? (row_ as VirtualItem).index : index_;
          const row = sortedData[index];
          const rowId = getRowId(row, index);
          return (
            <TableRow
              data-index={index}
              hover
              key={rowId}
              onDoubleClick={() => props.onRowDoubleClick?.(row, rowId)}
              ref={isVirtualization ? virtualizer.measureElement : undefined}
            >
              {selectable && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    ...getStickyStyle('__checkbox__', false),
                    borderBottom: '2px solid #e0e0e0',
                  }}
                >
                  <Checkbox
                    checked={selection?.includes(rowId) ?? false}
                    onChange={() => toggleRow(rowId)}
                  />
                </TableCell>
              )}
              {columns.map((col) => {
                const params: RenderCellParams<T> = {
                  field: col.field,
                  id: rowId,
                  row,
                  value: row[col.field],
                };
                return (
                  <TableCell
                    key={col.field as string}
                    sx={{
                      ...getStickyStyle(col.field, false),
                      ...getColWidthStyle(col.field),
                      ...BODY_CELL_SX,
                    }}
                  >
                    {col.renderCell
                      ? col.renderCell(params)
                      : String(params.value)}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </>
    );
  };

  const virtualPadding = ({ height }: { height: number }) =>
    height > 0 ? <TableRow aria-hidden sx={{ height }} /> : null;

  const renderRows = () => {
    const height = () => {
      const items = virtualizer.getVirtualItems();
      if (!items.length) return 0;
      return virtualizer.getTotalSize() - (items[items.length - 1].end ?? 0);
    };

    return isVirtualization ? (
      <>
        {virtualPadding({
          height: virtualizer.getVirtualItems()[0]?.start ?? 0,
        })}
        {tableRows()}
        {virtualPadding({ height: height() })}
      </>
    ) : (
      tableRows()
    );
  };

  return (
    <Box
      sx={{
        '.MuiTableRow-root.Mui-selected': {
          '.MuiTableCell-body': { background: 'rgb(235 244 252)' },
        },
        '.MuiTableRow-root:hover': {
          '.MuiTableCell-body': { background: 'rgb(230, 242, 252)' },
        },
        ...props.sx,
      }}
    >
      <TableContainer
        component={Paper}
        id="custom-table-header-container"
        sx={{
          overflow: 'hidden',
          position: 'sticky',
          top: isEmbedded() ? '0px' : HEADER_HEIGHT,
          zIndex: 5,
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell
                  data-field="__checkbox__"
                  padding="checkbox"
                  sx={{
                    ...getStickyStyle('__checkbox__', true),
                    ...HEADER_CELL_SX,
                  }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={!isAllSelected && Boolean(selection?.length)}
                    onChange={toggleAll}
                  />
                </TableCell>
              )}
              {columns.map((col, index) => {
                const renderHeader = col?.renderHeader
                  ? col?.renderHeader?.({
                      column: col,
                      field: col.field,
                      index,
                    })
                  : col.headerName;
                const appliedFilterOnCol: Filter = props.appliedFilters?.[
                  col.field as string
                ] as Filter;
                return (
                  <TableCell
                    data-field={col.field}
                    key={col.field as string}
                    sx={{
                      ...getStickyStyle(col.field, true),
                      ...getColWidthStyle(col.field),
                      ...HEADER_CELL_SX,
                    }}
                  >
                    <Stack alignItems="center" flexDirection="row" gap={1}>
                      {renderHeader}
                      {col.filters && (
                        <FilterComponent
                          appliedFilterOnCol={appliedFilterOnCol}
                          column={col}
                          filterBy={filterBy as keyof T}
                          onAutoCompleteValueChange={(v) =>
                            col.filters?.onAutoCompleteValueChange?.(v)
                          }
                          onFilterApply={props?.onFilterApply}
                          openFilters={openFilters}
                          setFilterBy={setFilterBy}
                          setOpenFilters={setOpenFilters}
                        />
                      )}
                      {col.sortable && (
                        <Stack
                          data-testid={`${col.field as string}-sort-arrow`}
                          id="sort-arrow"
                          onClick={() => handleSort(col.field)}
                          sx={{
                            cursor: 'pointer',
                          }}
                        >
                          <ArrowNarrowUp
                            size={18}
                            sx={{
                              '.MuiSvgIcon-root': {
                                color: 'rgb(100, 100, 100) !important',
                              },
                              '.MuiTableCell-head:hover &': {
                                opacity: orderBy === col.field ? 1 : 0.5,
                              },
                              opacity: orderBy === col.field ? 1 : 0,
                              transform:
                                order === 'asc'
                                  ? 'rotate(0deg)'
                                  : 'rotate(-180deg)',
                              transition: 'transform 0.3s',
                            }}
                          />
                        </Stack>
                      )}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
        </Table>
      </TableContainer>
      <Box
        ref={bodyContainerRef}
        sx={{
          ...(props.virtualizationOptions && {
            height: props.virtualizationOptions?.containerHeight ?? 500,
            overflow: 'auto',
            position: 'relative',
          }),
        }}
      >
        <TableContainer id="custom-table-body-container">
          <Table>
            <TableBody>
              {!isLoading && data.length === 0 && (
                <TableRow sx={{ height: 104 }}>
                  {selectable && <TableCell sx={BODY_CELL_SX} />}
                  {columns.map((col) => (
                    <TableCell
                      key={col.field as string}
                      sx={{
                        ...getColWidthStyle(col.field),
                      }}
                    />
                  ))}
                  <Typography
                    sx={{
                      fontWeight: 400,
                      left: '50%',
                      position: 'absolute',
                      top: `calc(${
                        bodyContainerRef.current?.getBoundingClientRect().top
                      }px + 50px)`,
                    }}
                    variant="error"
                  >
                    {customMessage || translate('Common.NoRecord')}
                  </Typography>
                </TableRow>
              )}
              {isLoading ? (
                <>
                  {Array.from({ length: 10 }, (_, index) => ({
                    id: index + 1,
                  })).map((_) => (
                    <TableSkeleton<T>
                      columns={columns}
                      getColWidthStyle={getColWidthStyle}
                      getStickyStyle={getStickyStyle}
                      key={_.id}
                      selectable={selectable}
                    />
                  ))}
                </>
              ) : (
                renderRows()
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {paginationComponent && data.length !== 0 && (
        <div>{paginationComponent}</div>
      )}
    </Box>
  );
};

const CustomTable = forwardRef(CustomTable_) as <T>(
  props: CustomTableProps<T> & { ref?: Ref<CustomTableHandle> }
) => ReactElement;

export default CustomTable;
